import {
  formatNumber,
  formatPrice,
  noDecimalsFormatOptions,
} from "../../../components"
import { isDefined, labelOf } from "../../../utils"
import { type PurchasesChannelName, purchasesChannelEnum } from "../../referential"
import type {
  ApiChannel,
  ApiPurchaseBase,
  ApiPurchasesSummary,
} from "./financialDataPurchases.model"

interface FormattedPurchases {
  mainStat: string
  evolution?: number
}

type FormattedDiscounts = Record<keyof ApiPurchaseBase["discounts"], string>

export type FormattedPurchasesSummary = Omit<
  ApiPurchaseBase,
  "purchases" | "discounts"
> & {
  purchases: FormattedPurchases
  discounts: FormattedDiscounts
}

type FormattedChannel = Pick<ApiChannel, "name"> &
  FormattedPurchasesSummary & {
    id: string
  }

interface FormattedContent extends FormattedPurchasesSummary {
  channel: FormattedChannel[]
}

export interface PurchasesSummaryResult {
  formattedData: {
    main: FormattedContent
    others: FormattedContent
    global: FormattedPurchasesSummary
  }
  api: ApiPurchasesSummary
}

const formatBasePurchases = (
  purchases?: ApiPurchaseBase,
): FormattedPurchasesSummary => ({
  ...purchases,
  discounts: {
    percentage: isDefined(purchases?.discounts?.percentage)
      ? formatNumber(purchases.discounts.percentage, noDecimalsFormatOptions)
      : "-",
    total: formatNumber(purchases?.discounts?.total ?? 0, noDecimalsFormatOptions),
  },
  purchases: {
    mainStat: isDefined(purchases?.purchases?.mainStat)
      ? formatPrice(purchases.purchases.mainStat, noDecimalsFormatOptions)
      : "-",
    evolution: purchases?.purchases?.evolution ?? undefined,
  },
  sources: purchases?.sources ?? [],
})

const formatChannelPurchases = (channel: ApiChannel): FormattedChannel => ({
  ...formatBasePurchases(channel),
  name: labelOf(purchasesChannelEnum, channel.name),
  id: channel.name,
})

export const mergeAndSortMainChannels = (
  channels: ApiChannel[],
  pinnedChannels: PurchasesChannelName[],
): ApiChannel[] =>
  [
    ...new Map<string, ApiChannel>([
      ...pinnedChannels.map((name) => [name, { name } as ApiChannel] as const),
      ...channels.map((channel) => [channel.name, channel] as const),
    ]).values(),
  ]
    // Sort following pinnedChannels order, with unpinned channels at the end
    .toSorted((a, b) => {
      const aIndex = (pinnedChannels as string[]).indexOf(a.name)
      const bIndex = (pinnedChannels as string[]).indexOf(b.name)
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })

export const selectPurchases = (
  rawData: ApiPurchasesSummary,
  pinnedChannels: PurchasesChannelName[],
): PurchasesSummaryResult => {
  const othersChannels = rawData.other.channels.map(formatChannelPurchases)

  const mainChannels = mergeAndSortMainChannels(
    rawData.main.channels,
    pinnedChannels,
  ).map(formatChannelPurchases)

  return {
    api: rawData,
    formattedData: {
      global: formatBasePurchases(rawData),
      main: {
        ...formatBasePurchases(rawData.main),
        channel: mainChannels,
      },
      others: {
        ...formatBasePurchases(rawData.other),
        channel: othersChannels,
      },
    },
  }
}
